import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import ExpandMore from '@mui/icons-material/esm/ExpandMore';
import ChevronRight from '@mui/icons-material/esm/ChevronRight';
import Folder from '@mui/icons-material/esm/Folder';
import FolderOpen from '@mui/icons-material/esm/FolderOpen';
import Description from '@mui/icons-material/esm/Description';
import Flag from '@mui/icons-material/esm/Flag';
import MoreVert from '@mui/icons-material/esm/MoreVert';
import Add from '@mui/icons-material/esm/Add';
import Edit from '@mui/icons-material/esm/Edit';
import Delete from '@mui/icons-material/esm/Delete';
import LinkIcon from '@mui/icons-material/esm/Link';
import MoveUp from '@mui/icons-material/esm/MoveUp';
import DashboardIcon from '@mui/icons-material/esm/Dashboard';
import { StatusBadge, ProgressBar } from '../common';
import { WBSTreeNode as WBSTreeNodeType } from '../../services/api/wbsService';

export interface WBSTreeNodeProps {
  node: WBSTreeNodeType;
  onExpand?: (nodeId: string) => void;
  onCollapse?: (nodeId: string) => void;
  onClick?: (node: WBSTreeNodeType) => void;
  onAddChild?: (parentNode: WBSTreeNodeType) => void;
  onEdit?: (node: WBSTreeNodeType) => void;
  onDelete?: (node: WBSTreeNodeType) => void;
  onMove?: (node: WBSTreeNodeType) => void;
  onLinkTasks?: (node: WBSTreeNodeType) => void;
  onViewEVM?: (node: WBSTreeNodeType) => void;
  level?: number;
}

export default function WBSTreeNode({
  node,
  onExpand,
  onCollapse,
  onClick,
  onAddChild,
  onEdit,
  onDelete,
  onMove,
  onLinkTasks,
  onViewEVM,
  level = 0,
}: WBSTreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(node.isExpanded ?? true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const contextMenuOpen = Boolean(anchorEl);

  const hasChildren = node.children && node.children.length > 0;
  const hasActions = Boolean(onAddChild || onEdit || onMove || onLinkTasks || onViewEVM || onDelete);

  const handleToggle = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    if (newExpanded) {
      onExpand?.(node.id);
    } else {
      onCollapse?.(node.id);
    }
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (action: () => void) => {
    handleCloseMenu();
    action();
  };

  // Get icon based on type
  const getIcon = () => {
    if (node.type === 'MILESTONE') {
      return <Flag sx={{ fontSize: 20, color: 'warning.main' }} />;
    }
    if (node.type === 'WORK_PACKAGE') {
      return <Description sx={{ fontSize: 20, color: 'primary.main' }} />;
    }
    // PROJECT, PHASE, DELIVERABLE
    return isExpanded ? (
      <FolderOpen sx={{ fontSize: 20, color: 'info.main' }} />
    ) : (
      <Folder sx={{ fontSize: 20, color: 'info.main' }} />
    );
  };

  // Get color for progress bar based on status
  const getProgressColor = (): 'primary' | 'success' | 'warning' | 'error' => {
    if (node.status === 'COMPLETED') return 'success';
    if (node.status === 'CANCELLED') return 'error';
    if (node.performanceStatus === 'BEHIND_AND_OVER') return 'error';
    if (node.performanceStatus === 'BEHIND_SCHEDULE' || node.performanceStatus === 'OVER_BUDGET')
      return 'warning';
    return 'primary';
  };

  return (
    <Box>
      {/* Node Container */}
      <Box
        sx={{
          pl: level * 3,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover',
          },
          transition: 'background-color 0.2s',
        }}
        onClick={() => onClick?.(node)}
      >
        {/* Expand/Collapse Button */}
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
            sx={{ p: 0.5 }}
          >
            {isExpanded ? <ExpandMore /> : <ChevronRight />}
          </IconButton>
        ) : (
          <Box sx={{ width: 32 }} />
        )}

        {/* Icon */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>{getIcon()}</Box>

        {/* Code */}
        <Typography
          variant="body2"
          fontWeight="bold"
          sx={{ minWidth: 80, color: 'text.secondary' }}
        >
          {node.code}
        </Typography>

        {/* Name */}
        <Typography variant="body2" fontWeight="medium" sx={{ flexGrow: 1, minWidth: 200 }}>
          {node.name}
        </Typography>

        {/* Status Badge */}
        <StatusBadge status={node.status} type="custom" customColor="primary" size="small" />

        {/* Priority Badge */}
        {node.priority && (
          <StatusBadge
            status={node.priority}
            type="priority"
            variant="outlined"
            size="small"
          />
        )}

        {/* Progress */}
        <Box sx={{ width: 150, mr: 2 }}>
          <ProgressBar
            value={node.progress}
            height={6}
            color={getProgressColor()}
            showPercentage={false}
          />
        </Box>
        <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'right' }}>
          {node.progress}%
        </Typography>

        {/* Budget & EV (if applicable) */}
        {node.budget > 0 && (
          <Box sx={{ display: 'flex', gap: 2, mr: 2 }}>
            <Tooltip title="Budget">
              <Typography variant="caption" color="text.secondary">
                ${(node.budget / 1000).toFixed(0)}K
              </Typography>
            </Tooltip>
            <Tooltip title="Earned Value">
              <Typography variant="caption" color="primary.main" fontWeight="medium">
                EV: ${(node.earnedValue / 1000).toFixed(0)}K
              </Typography>
            </Tooltip>
          </Box>
        )}

        {/* CPI/SPI Indicators */}
        {node.cpi !== undefined && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Cost Performance Index">
              <Typography
                variant="caption"
                sx={{
                  color: node.cpi >= 1 ? 'success.main' : node.cpi >= 0.9 ? 'warning.main' : 'error.main',
                  fontWeight: 'medium',
                }}
              >
                CPI: {node.cpi.toFixed(2)}
              </Typography>
            </Tooltip>
            <Tooltip title="Schedule Performance Index">
              <Typography
                variant="caption"
                sx={{
                  color: node.spi && node.spi >= 1 ? 'success.main' : node.spi && node.spi >= 0.9 ? 'warning.main' : 'error.main',
                  fontWeight: 'medium',
                }}
              >
                SPI: {node.spi?.toFixed(2)}
              </Typography>
            </Tooltip>
          </Box>
        )}

        {/* Task Count */}
        {node.taskCount !== undefined && node.taskCount > 0 && (
          <Tooltip title={`${node.taskCount} tasks linked`}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              {node.taskCount} tasks
            </Typography>
          </Tooltip>
        )}

        {/* Context Menu Button */}
        {hasActions && (
          <IconButton size="small" onClick={handleContextMenu} sx={{ p: 0.5 }}>
            <MoreVert fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={contextMenuOpen} onClose={handleCloseMenu}>
        {onAddChild && <MenuItem onClick={() => handleMenuAction(() => onAddChild(node))}>
          <ListItemIcon>
            <Add fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add Child Element</ListItemText>
        </MenuItem>}
        {onEdit && <MenuItem onClick={() => handleMenuAction(() => onEdit(node))}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Element</ListItemText>
        </MenuItem>}
        {onMove && <MenuItem onClick={() => handleMenuAction(() => onMove(node))}>
          <ListItemIcon>
            <MoveUp fontSize="small" />
          </ListItemIcon>
          <ListItemText>Move Element</ListItemText>
        </MenuItem>}
        {onLinkTasks && <MenuItem onClick={() => handleMenuAction(() => onLinkTasks(node))}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Link Tasks</ListItemText>
        </MenuItem>}
        {onViewEVM && <MenuItem onClick={() => handleMenuAction(() => onViewEVM(node))}>
          <ListItemIcon>
            <DashboardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View EVM Dashboard</ListItemText>
        </MenuItem>}
        {onDelete && <MenuItem
          onClick={() => handleMenuAction(() => onDelete?.(node))}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Element</ListItemText>
        </MenuItem>}
      </Menu>

      {/* Children (Recursive) */}
      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {node.children!.map((child) => (
            <WBSTreeNode
              key={child.id}
              node={child}
              onExpand={onExpand}
              onCollapse={onCollapse}
              onClick={onClick}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
              onMove={onMove}
              onLinkTasks={onLinkTasks}
              onViewEVM={onViewEVM}
              level={level + 1}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
}
